import { useEffect, type FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import { applyManualLanguage } from '../../../lib/i18n/i18n.ts';
import { flaggedLanguages, isSupportedLanguage } from '../../../lib/i18n/language-config.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';

/**
 * Headless consumer of the `?lng=` param that LanguageRedirect carries over
 * from the legacy /en and /de prefixes. The redirect itself cannot know the
 * tenant, so the flag decision lands here:
 *
 * - `enableSettingsMenu` off: pre-STEER-184 behavior — the link switches the
 *   language and the choice persists, exactly as the old routes did.
 * - on: the language is left alone and the visit is only reported, to measure
 *   whether such links are still in circulation.
 *
 * The param is consumed either way so reloads and copied URLs stay clean.
 */
export const LegacyLanguageUrl: FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { enableSettingsMenu } = useAppConfig();
    const sendEvent = useAnalytics();

    const urlLanguage = searchParams.get('lng');

    useEffect(() => {
        if (urlLanguage === null) {
            return;
        }

        setSearchParams(
            (previous) => {
                previous.delete('lng');
                return previous;
            },
            { replace: true },
        );

        if (!isSupportedLanguage(urlLanguage)) {
            return;
        }
        if (enableSettingsMenu) {
            sendEvent('legacy_language_url_visited', {
                url_language: urlLanguage,
                path: window.location.pathname,
            });
        } else if (!flaggedLanguages.includes(urlLanguage)) {
            // A flagged language (e.g. /fr) rides enableSettingsMenu, which is
            // off on this branch — the link does nothing.
            applyManualLanguage(urlLanguage);
        }
    }, [urlLanguage, enableSettingsMenu, setSearchParams, sendEvent]);

    return null;
};
